import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { InlineNotificationProps } from './inline-notification.component';

type InlineNotificationStylesProps = {
  status: InlineNotificationProps['status'];
  size: InlineNotificationProps['size'];
  maxWidth?: InlineNotificationProps['maxWidth'];
};

const paddingSize = {
  large: theme.spacing(2),
  medium: theme.spacing(1),
  small: theme.spacing(0.5),
} as const;

const maxWidthSize = {
  large: '700px',
  medium: '700px',
  small: '450px',
} as const;

const filledStartBorderWidth = {
  large: '8px',
  medium: '6px',
  small: '4px',
} as const;

const backgroundColor = {
  warn: theme.colors.warning5,
  success: theme.colors.success5,
  error: theme.colors.critical5,
  info: theme.colors.primary5,
  premium: theme.colorsV2.seaweed50(10),
} as const;

const borderColor = {
  warn: theme.colors.warning50,
  success: theme.colors.success50,
  error: theme.colors.critical50,
  info: theme.colors.primary50,
  premium: theme.colors.secondary.seaweed50,
} as const;

const borderRadius = {
  large: theme.borderRadius.medium,
  medium: theme.borderRadius.medium,
  small: theme.borderRadius.small,
} as const;

export const containerStyles = ({ status, size = 'large', maxWidth }: InlineNotificationStylesProps) => {
  return css`
    display: flex;
    min-height: ${size === 'small' ? '24px' : size === 'medium' ? '40px' : '72px'};
    max-width: ${maxWidth ? maxWidth : maxWidthSize[size]};
    position: relative;

    &:before {
      content: '';
      width: ${filledStartBorderWidth[size]};
      height: 100%;
      background-color: ${borderColor[status]};
      border-radius: ${borderRadius[size]} 0 0 ${borderRadius[size]};
      flex-shrink: 0;
      position: absolute;
      top: 0;
      left: 0;
    }
  `;
};

export const contentContainerStyles = ({ status, size = 'large' }: InlineNotificationStylesProps) => {
  return css`
    background-color: ${backgroundColor[status]};
    padding: ${paddingSize[size]};
    padding-left: calc(
      ${paddingSize[size]} + ${filledStartBorderWidth[size]}
    ); // Added padding to the left to account for the filled start border width
    color: ${theme.colors.neutral90};
    display: flex;
    align-items: center;
    width: fit-content;
    max-width: 100%;
    gap: ${paddingSize[size]};
    border-radius: ${borderRadius[size]};
    border: ${size === 'small' ? 'none' : `1px solid ${borderColor[status]}`};

    > :first-child {
      flex-shrink: 0;
    }
  `;
};
